import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { BehaviorSubject, Observer } from "rxjs";
import { Router } from "@angular/router";

import { ByDepartment } from "../../../../model/filters/by-department";
import { MatchAny } from "../../../../model/filters/match.any";
import { Filter } from "../../../../model/filters/filter";
import { SingleChoice } from "../../../../model/singlechoice";
import { DepartmentsService } from "../../../../services/departaments.service";
import { NavigationHelper } from "../../../../services/navigation.helper";
import { FilterService } from "../../../../services/filter.service";
import { Navigation } from "../../../../model/navigation";
import { Labeler } from "../../../../model/labeler";
import { NotComponent } from "../not/not.component";

@Component({
  selector: "by-department-form",
  templateUrl: "./by-department-form.component.html",
  styleUrl: "./by-department-form.component.css",
})
export class ByDepartmentFormComponent implements OnInit {
  @Input() filter: ByDepartment = new ByDepartment();
  @Output() applyFilter = new EventEmitter<ByDepartment>();
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();

  byDepartmentForm: FormGroup;

  editingIndex: number | null = null;
  departmentLabeler = new BehaviorSubject<string>("-");
  completionUrl = new BehaviorSubject<string>(
    "tickets/-1/filter/by.department"
  );
  currentId: number | null = null;
  byDepartmentFilter: ByDepartment = new ByDepartment();
  @ViewChild(NotComponent) notComponent!: NotComponent;

  constructor(
    private fb: FormBuilder,
    private departmentService: DepartmentsService,
    private nav: NavigationHelper,
    private filterService: FilterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const existingFilters =
      this.filter instanceof MatchAny ? this.filter.filters : [this.filter];

    const departmentIdsFromFilter: number[] = existingFilters
      .filter((f): f is ByDepartment => f instanceof ByDepartment)
      .map((f) => f.id)
      .filter((id) => id !== undefined) as number[];

    const storedIds = localStorage.getItem("departmentIds");
    const departmentIdsFromStorage = storedIds
      ? (JSON.parse(storedIds) as number[])
      : [];

    const mergedIds = Array.from(
      new Set([...departmentIdsFromFilter, ...departmentIdsFromStorage])
    );

    this.initializeForm(mergedIds);

    if (mergedIds.length > 0) {
      this.initializeForm(mergedIds);
    } else {
      this.byDepartmentForm = this.fb.group({
        departments: this.fb.array([
          this.fb.control(null, Validators.required),
        ]),
      });
    }

    if (mergedIds.length > 0) {
      this.currentId = mergedIds[0];
      this.departmentLabeler.next(
        this.departmentService.labelForId(this.currentId)
      );
    }
  }

  initializeForm(departmentIds: number[]): void {
    const controls = departmentIds.map((id) =>
      this.fb.control(id, Validators.required)
    );
    if (controls.length === 0)
      controls.push(this.fb.control(null, Validators.required));

    this.byDepartmentForm = this.fb.group({
      departments: this.fb.array(controls),
    });

    this.currentId = controls[0]?.value ?? null;
    if (this.currentId !== null) {
      this.departmentLabeler.next(
        this.departmentService.labelForId(this.currentId)
      );
    }
  }

  restoreDepartments(ids: (number | null)[]): void {
    this.byDepartmentForm.setControl("departments", this.fb.array([]));
    for (const id of ids) {
      this.departments.push(this.fb.control(id, Validators.required));
    }
  }

  apply(): void {
    const ids: number[] = this.departments.value.filter(
      (id: number | null | undefined): id is number =>
        id !== null && id !== undefined
    );

    if (ids.length === 0) return;

    localStorage.setItem("departmentIds", JSON.stringify(ids));

    let finalFilter: Filter;
    if (ids.length === 1) {
      finalFilter = new ByDepartment(ids[0]);
    } else {
      finalFilter = new MatchAny(ids.map((id) => new ByDepartment(id)));
    }

    this.notComponent.filter = finalFilter;
    const modifiedFilter = this.notComponent.getModifiedFilter();

    this.applyFilter.emit(modifiedFilter);
 
  }

  onFilterChanged(finalFilter: Filter) {
    this.applyFilter.emit(finalFilter);
    
  }

  onDepartmentChoice(id: number): void {
    if (this.editingIndex !== null && this.departments.at(this.editingIndex)) {
      this.departments.at(this.editingIndex).setValue(id);
    } else if (this.departments.length > 0) {
      this.departments.at(0).setValue(id);
    }

    const currentValues = this.departments.value.filter((id) => id !== null);
    localStorage.setItem("departmentIds", JSON.stringify(currentValues));

    this.currentId = id;

    this.router.navigate(["tickets/-1/filter/by.department/" + id]).then(() => {
      this.nav.freshNavigation;
    });
  }

  addDepartmentField(): void {
    this.departments.push(this.fb.control(null, Validators.required));

    const currentValues = this.departments.value.filter((id) => id !== null);
    localStorage.setItem("departmentIds", JSON.stringify(currentValues));
  }

  setEditingIndex(index: number): void {
    this.editingIndex = index;
    localStorage.setItem("editingDepartmentIndex", index.toString());
  }

  relaySingleChoice(choice: SingleChoice) {
    this.nav.startChoice(choice);
  }

  get departments(): FormArray<FormControl> {
    return this.byDepartmentForm.get("departments") as FormArray<FormControl>;
  }

  get departmentObserver(): Partial<Observer<number>> {
    return {
      next: (id) => {
        this.onDepartmentChoice(id);
      },
    };
  }

  get fakeId(): number {
    return Navigation.NEW;
  }

  get departmentLabelerByService(): Labeler {
    return this.departmentService;
  }
}
